import React, { useEffect, useRef, useState } from 'react';
import { Button, Space } from 'antd';
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import styles from './index.less';
import { ProForm } from '@ant-design/pro-form';
import { LeadTaskModel } from '@/typings/models/LeadTask';
import { history, useDispatch, useSelector } from 'umi';
import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import dayjs from 'dayjs';
import CreateTaskForm from '@/pages/Tasks/CreateTaskForm';
import * as LeadTaskSelectors from '@/selectors/leadTaskSelectors';
import { getTaskLead, getTaskOwners } from '@/utils/task';

type ICreateTaskDrawerProps = {
  updateRequest?: () => void;
  leadId?: string;
  task?: LeadTaskModel;
  open?: boolean
  onVisibleChange?: (visible: boolean) => void
};
const CreateTaskDrawer: React.FC<ICreateTaskDrawerProps> = () => {
  const dispatch = useDispatch();
  const open = useSelector(LeadTaskSelectors.selectCreateTaskDrawerOpen);
  const task = useSelector(LeadTaskSelectors.selectCreateTaskDrawerTask) as LeadTaskModel | undefined;
  const leadId = useSelector(LeadTaskSelectors.selectCreateTaskDrawerLeadId) as string | undefined;
  const options = useSelector(
    LeadTaskSelectors.selectCreateTaskDrawerOptions,
  );
  const callback = useSelector(LeadTaskSelectors.selectCreateTaskDrawerOnCreated) as Function;

  const [loading, setLoading] = useState(false);
  const [form] = ProForm.useForm();
  const taskId = task?.id;

  const handleCancel = () => {
    dispatch({ type: 'leadTasks/closeCreateTaskDrawer' });
  };

  const previousRoute = useRef<string | null>(null);
  useEffect(() => {
    if (open && taskId) {
      previousRoute.current = window.location.pathname;
      window.history.pushState({}, '', `/task/${taskId}`);
    } else if (previousRoute.current) {
      if (previousRoute.current.includes('/task/'))
        return history.push('/tasks');
      window.history.pushState({}, '', previousRoute.current);
    }

  }, [open, taskId]);

  useEffect(() => {
    if (open && form) {
      form.resetFields();
      if (task?.id) {
        form.setFieldsValue({
          ...task,
          // Only actual user owners — a lead-owned task has no reassignable users
          assignee_ids: getTaskOwners(task)
            .filter((owner) => owner.type === 'user')
            .map((owner) => (owner.data as any)?.id)
            .filter(Boolean),
          description: (task as any).description_summary,
        });
      } else {
        form.setFieldsValue({
          title: options.title,
          due_date: dayjs().add(options.dueDateInDays || 1, 'days').startOf('day'),
        });
      }
    }
  }, [open, task, form]);

  const taskLead = getTaskLead(task);
  const effectiveLeadId = taskLead?.id || leadId;

  const title = task?.id ? (
    <F entity={INTL_ENTITY.TASK} action={INTL_ACTION.EDIT} />
  ) : (
    <F entity={INTL_ENTITY.TASK} action={INTL_ACTION.CREATE} />
  );
  const submitOnEnter = useSubmitOnEnter(() => form.submit(), {
    enabled: !loading,
  });

  return (
    <>
      <Drawer
        wrapClassName={styles.createTaskModalWrap}
        title={title}
        open={open}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button className={'button-secondary button-bordered'} size={'large'} onClick={handleCancel}>
                <F id={'common.actions.cancel'} defaultMessage={'Cancel'} />
              </Button>
              <Button size={'large'} type="primary" loading={loading} onClick={() => form.submit()}>
                {title}
              </Button>
            </Space>
          </div>
        }
        destroyOnHidden
        onOpenChange={handleCancel}
      >
        <div {...submitOnEnter}>
          <CreateTaskForm
            leadId={effectiveLeadId}
            fallbackLead={taskLead}
            form={form}
            setLoading={setLoading}
            task={task as any}
            onTaskCreated={() => {
              handleCancel();
              callback && callback();
            }}
          />
        </div>
      </Drawer>
    </>
  );
};

export default CreateTaskDrawer;
